import { collection, doc, getDoc, getDocs, limit, orderBy, query, where, Timestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "./firebase";

// Announcements (spec 044 D39–D48): `announcements/{id}` is read by every staff role and
// written only by the functions below. Errors are rethrown, never swallowed, like the other
// staff doors in dataService.js — a failure must never look like success.

const DAYS_90 = 90 * 24 * 60 * 60 * 1000;

// The History list (044 D40, ruling R2): everything touched in the last 90 days, newest edit first.
// ponytail: filtered and paged in the browser; server paging once a quarter holds 500+.
export const listAnnouncements = async (now = new Date()) => {
  const since = Timestamp.fromDate(new Date(now.getTime() - DAYS_90));
  const snap = await getDocs(
    query(collection(db, "announcements"), where("updatedAt", ">=", since), orderBy("updatedAt", "desc"), limit(500))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

// One announcement, or null when it no longer exists.
export const getAnnouncement = async (id) => {
  const snap = await getDoc(doc(db, "announcements", id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

const callable = (name, options) => async (payload) => {
  const { data } = await httpsCallable(functions, name, options)(payload);
  return data;
};

// { id?, action: "draft" | "schedule" | "send", title, body, url, audience, sendAt? }
// → { id, status, counts }. A send runs inline, so the call may take minutes.
export const saveAnnouncement = callable("saveAnnouncement", { timeout: 540000 });
// { id } → { id }; a draft or a scheduled one only.
export const deleteAnnouncement = callable("deleteAnnouncement");
// { audience } → { targeted, withDevice }.
export const previewAnnouncementAudience = callable("previewAnnouncementAudience");
// { ids } → { opened: { [id]: number } }. The callable takes 25 ids a call; a longer list is
// split here and read in parallel. A failed part only leaves its own ids out; the call fails
// only when every part does.
const STATS_IDS = 25;
const statsCall = callable("announcementStats");
export const announcementStats = async ({ ids }) => {
  const parts = [];
  for (let i = 0; i < ids.length; i += STATS_IDS) parts.push(statsCall({ ids: ids.slice(i, i + STATS_IDS) }));
  const settled = await Promise.allSettled(parts);
  const ok = settled.filter((r) => r.status === "fulfilled");
  if (ok.length === 0) throw settled[0].reason;
  return { opened: Object.assign({}, ...ok.map((r) => r.value?.opened || {})) };
};
